import formatPhone from './formatPhone'
import { getOrganizationFromSubdomain } from './getOrganizationFromSubdomain'
import { storeRegistrationAllowCheckFlag } from './paymentWebSettings'

/** Subdomain of the portal support opened; the browser is on the support host, not the school's. */
export const SUPPORT_PARENT_PORTAL_KEY = 'supportParentPortal'
/** Set by logout when it ends a parent impersonation (Logout or token expiry); cleared by the next login. */
export const SUPPORT_PARENT_SESSION_ENDED_KEY = 'supportParentSessionEnded'

/** Where to go when a parent session ends. Support Exit must return to the support picker. */
export function pathWhenParentSessionEnds(): '/support-login' | '/parents/login' {
    if (typeof localStorage === 'undefined') return '/parents/login'
    if (localStorage.getItem('role') === 'support') return '/support-login'
    if (localStorage.getItem('supportShowPicker') === 'true') return '/support-login'
    if (localStorage.getItem(SUPPORT_PARENT_SESSION_ENDED_KEY) === 'true') return '/support-login'
    return '/parents/login'
}

/** The parent portal's subdomain: the one support picked while impersonating, else this host's. */
export function parentPortalSubdomain(): string {
    const impersonating = localStorage.getItem('role') === 'parent' && localStorage.getItem('isImpersonating') === 'true'
    const picked = impersonating ? (localStorage.getItem(SUPPORT_PARENT_PORTAL_KEY) || '').trim().toLowerCase() : ''
    return picked || getOrganizationFromSubdomain()
}

export function canSearchParents(query: string): boolean {
    const q = (query || '').trim()
    if (q.length === 0) return false
    if (/^\d+$/.test(q)) return true
    return q.length >= 2
}

export function parentResultLabel(parent: {
    firstName?: string
    lastName?: string
    FirstName?: string
    LastName?: string
    email?: string
    Email?: string
    displayName?: string
    DisplayName?: string
}): string {
    const display = (parent.displayName || parent.DisplayName || '').trim()
    const first = parent.firstName || parent.FirstName || ''
    const last = parent.lastName || parent.LastName || ''
    const email = (parent.email || parent.Email || '').trim()
    const name = display || `${first} ${last}`.trim()
    if (name && email) return `${name} (${email})`
    return name || email || 'Parent'
}

export function readField(row: Record<string, unknown>, camel: string, pascal: string): string {
    const value = row[camel] ?? row[pascal]
    return value == null ? '' : String(value)
}

export function readId(row: Record<string, unknown>, camel: string, pascal: string): number {
    const value = row[camel] ?? row[pascal]
    const n = typeof value === 'number' ? value : parseInt(String(value ?? ''), 10)
    return Number.isFinite(n) ? n : 0
}

export type ParentPortalSettings = {
    Email?: string | null
    email?: string | null
    Phone?: string | null
    phone?: string | null
    SchoolName?: string | null
    schoolName?: string | null
    Address?: string | null
    address?: string | null
    SchoolYear?: string | null
    schoolYear?: string | null
    ShowRegistration?: boolean | string | null
    showRegistration?: boolean | string | null
    AllowCheck?: boolean | string | null
    allowCheck?: boolean | string | null
    AllowAddPayment?: boolean | string | null
    allowAddPayment?: boolean | string | null
}

function firstDefined<T>(...values: (T | null | undefined)[]): T | undefined {
    for (let i = 0; i < values.length; i += 1) {
        if (values[i] !== undefined && values[i] !== null) return values[i] as T
    }
    return undefined
}

function text(value: unknown): string {
    return value == null ? '' : String(value)
}

/** Same strings ParentLogin writes, so the menu checks (`=== 'true'`, `!== 'false'`) match. */
function storedFlag(value: boolean | string | null | undefined, fallback: boolean): string {
    if (value === true || value === 'true' || value === 'True') return 'true'
    if (value === false || value === 'false' || value === 'False') return 'false'
    return fallback ? 'true' : 'false'
}

/**
 * Remember the portal switches a parent login would have loaded:
 * Registration in the menu, and whether Pay is allowed.
 */
export function storeParentPortalSettings(settings: ParentPortalSettings | null | undefined, subdomain: string): void {
    const data = settings || {}
    localStorage.setItem('SchoolEmail', text(firstDefined(data.Email, data.email)))
    localStorage.setItem('SchoolPhone', formatPhone(text(firstDefined(data.Phone, data.phone))) || '')
    localStorage.setItem('SchoolName', text(firstDefined(data.SchoolName, data.schoolName)))
    localStorage.setItem('SchoolAddress', text(firstDefined(data.Address, data.address)))
    localStorage.setItem('SchoolYear', text(firstDefined(data.SchoolYear, data.schoolYear)))
    localStorage.setItem('ShowRegistration', storedFlag(firstDefined(data.ShowRegistration, data.showRegistration), false))
    storeRegistrationAllowCheckFlag(firstDefined(data.AllowCheck, data.allowCheck), subdomain)
    localStorage.setItem('AllowAddPayment', storedFlag(firstDefined(data.AllowAddPayment, data.allowAddPayment), true))
}

/**
 * Remember the portal support opened, and load its api/getSchoolSettings.
 * A failed load hides Registration and Pay.
 */
export async function loadParentPortalSettings(baseUrl: string, subdomain: string): Promise<void> {
    const portal = (subdomain || '').trim()
    localStorage.setItem(SUPPORT_PARENT_PORTAL_KEY, portal)
    const hidden: ParentPortalSettings = { ShowRegistration: false, AllowAddPayment: false }
    if (!portal) {
        storeParentPortalSettings(hidden, '')
        return
    }
    const root = baseUrl && baseUrl.charAt(baseUrl.length - 1) === '/' ? baseUrl : (baseUrl || '') + '/'
    try {
        const response = await fetch(root + 'api/getSchoolSettings?subdomain=' + encodeURIComponent(portal))
        const data = await response.json().catch(() => null)
        if (!response.ok || !data || typeof data !== 'object') {
            storeParentPortalSettings(hidden, portal)
            return
        }
        storeParentPortalSettings(data as ParentPortalSettings, portal)
    } catch (e) {
        storeParentPortalSettings(hidden, portal)
    }
}
